import type { AIContentClassification, DisclosureCopy, SupportedAIApp } from '../types';

const BASE: Record<AIContentClassification, DisclosureCopy> = {
  AI_ASSISTED: {
    label: 'AI enhanced',
    description: 'Artificial intelligence was used to enhance this image.',
  },
  AI_MODIFIED: {
    label: 'AI modified',
    description: 'This image has been modified using artificial intelligence.',
  },
  AI_GENERATED: {
    label: 'AI generated',
    description: 'This media was generated using artificial intelligence.',
  },
};

/** Operation- and app-specific copy overrides. */
const OPERATION_COPY: Record<string, Partial<DisclosureCopy>> = {
  hyperdusk_dusk: {
    label: 'AI modified',
    description:
      'This image has been modified using artificial intelligence to create a dusk/twilight appearance.',
    supporting: 'Original property photograph used as the source image.',
  },
  hyperdusk_animation: {
    label: 'AI generated',
    description:
      'This video was generated using artificial intelligence from a dusk-modified source image.',
  },
  virtual_staging: {
    label: 'AI modified',
    description: 'This image has been digitally staged using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  virtual_staging_clear_furniture: {
    label: 'AI modified',
    description: 'This image has been digitally edited using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  virtual_staging_match_style: {
    label: 'AI modified',
    description: 'This image has been digitally staged using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  virtual_staging_same_room: {
    label: 'AI modified',
    description: 'This image has been digitally staged using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  virtual_staging_360: {
    label: 'AI modified',
    description: 'This 360° image has been digitally staged using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  virtual_staging_from_scratch: {
    label: 'AI generated',
    description: 'This image was generated using artificial intelligence.',
  },
  imagemotion_clip: {
    label: 'AI generated',
    description: 'This video was generated using artificial intelligence from a source image.',
  },
  imagemotion_compose: {
    label: 'AI generated',
    description: 'This video was generated using artificial intelligence from a source image.',
  },
  photoclear_blue_sky: {
    label: 'AI modified',
    description: 'The sky in this image has been replaced using artificial intelligence.',
  },
  photoclear_sunset_sky: {
    label: 'AI modified',
    description: 'The sky in this image has been replaced using artificial intelligence.',
  },
  photoclear_brighten_enhance: {
    label: 'AI enhanced',
    description: 'Artificial intelligence was used to enhance lighting and colour in this image.',
  },
};

const APP_FALLBACK: Partial<Record<SupportedAIApp, Partial<DisclosureCopy>>> = {
  hyperdusk: {
    description: 'This image has been modified using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  staging: {
    description: 'This image has been digitally staged using artificial intelligence.',
    supporting: 'Original property photograph used as the source image.',
  },
  imagemotion: {
    description: 'This video was generated using artificial intelligence from a source image.',
  },
};

export function getDisclosureCopy(opts: {
  classification: AIContentClassification;
  operation?: string;
  app?: SupportedAIApp;
}): DisclosureCopy {
  const base = BASE[opts.classification];
  const fromOp = opts.operation ? OPERATION_COPY[opts.operation] : undefined;
  const fromApp = opts.app ? APP_FALLBACK[opts.app] : undefined;

  return {
    label: fromOp?.label ?? base.label,
    description: fromOp?.description ?? fromApp?.description ?? base.description,
    supporting: fromOp?.supporting ?? fromApp?.supporting,
  };
}

/** Burn-in / download corner label text matching classification. */
export function getDownloadLabel(classification: AIContentClassification): string {
  switch (classification) {
    case 'AI_ASSISTED':
      return 'AI Enhanced';
    case 'AI_MODIFIED':
      return 'AI Modified';
    case 'AI_GENERATED':
      return 'AI Generated';
  }
}
